import React, { useEffect, useRef, useState } from "react";
import { API } from "../lib/apiClient";
import { Send, Bot, User as UserIcon } from "lucide-react";
import { Button } from "./ui/button";

const stripLead = (t) => t.replace(/###LEAD###[\s\S]*?(###LEAD###)?$/g, "").trim();

export const ChatWidget = ({ slug, onLeadUpdate }) => {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const scrollRef = useRef(null);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [kb, setKb] = useState(false);

  const isMobile = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;

  // Keep the widget (and its input) above the on-screen keyboard on mobile.
  useEffect(() => {
    const vv = window.visualViewport;
    const apply = () => {
      const r = rootRef.current;
      if (!r) return;
      if (kb && vv) {
        r.style.top = `${vv.offsetTop}px`;
        r.style.height = `${vv.height}px`;
      } else {
        r.style.top = "";
        r.style.height = "";
      }
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
    };
    apply();
    if (vv) {
      vv.addEventListener("resize", apply);
      vv.addEventListener("scroll", apply);
      return () => { vv.removeEventListener("resize", apply); vv.removeEventListener("scroll", apply); };
    }
  }, [kb]);

  const handleFocus = () => { if (isMobile()) setKb(true); };
  const handleBlur = () => setKb(false);

  useEffect(() => {
    if (!slug) return;
    const start = async () => {
      const res = await fetch(`${API}/chat/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug }),
      });
      const data = await res.json();
      if (!res.ok) {
        setBlocked(true);
        setMessages([{ role: "assistant", content: data.detail || "This receptionist is currently unavailable. Please try again later." }]);
        return;
      }
      setSessionId(data.chat_session_id);
      setMessages([{ role: "assistant", content: data.greeting }]);
    };
    start();
  }, [slug]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming || !sessionId || blocked) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chat_session_id: sessionId, message: text }),
      });
      if (!res.ok) {
        let detail = "Sorry, something went wrong. Please try again.";
        try { detail = (await res.json()).detail || detail; } catch {}
        if (res.status === 402 || res.status === 429) setBlocked(true);
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: detail };
          return copy;
        });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: stripLead(copy[copy.length - 1].content + evt.delta) };
                return copy;
              });
            }
            if (evt.done) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: evt.full || stripLead(copy[copy.length - 1].content) };
                return copy;
              });
              onLeadUpdate?.();
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, something went wrong. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div ref={rootRef} className={`flex flex-col bg-[#0f0f11] border border-white/10 overflow-hidden ${kb ? "fixed left-0 right-0 z-[60] rounded-none border-x-0 border-t-0" : "h-full rounded-sm"}`} data-testid="chat-widget">
      <div className="flex items-center gap-3 px-5 py-4 bg-[#18181b] border-b border-white/10">
        <div className="h-9 w-9 rounded-sm bg-primary flex items-center justify-center">
          <Bot className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="font-display text-lg leading-none tracking-wide text-foreground">AVA</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> AI Receptionist · Online
          </p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`h-7 w-7 shrink-0 rounded-sm flex items-center justify-center ${m.role === "user" ? "bg-secondary" : "bg-primary"}`}>
              {m.role === "user" ? <UserIcon className="h-4 w-4 text-foreground" /> : <Bot className="h-4 w-4 text-white" />}
            </div>
            <div className={`max-w-[78%] px-4 py-2.5 text-sm leading-relaxed rounded-sm ${m.role === "user" ? "bg-secondary text-foreground" : "bg-[#1c1c1f] text-foreground border border-white/5"}`}>
              {m.content || (streaming && i === messages.length - 1 ? (
                <span className="flex gap-1">
                  <span className="typing-dot h-1.5 w-1.5 bg-muted-foreground rounded-full" />
                  <span className="typing-dot h-1.5 w-1.5 bg-muted-foreground rounded-full" style={{ animationDelay: "0.2s" }} />
                  <span className="typing-dot h-1.5 w-1.5 bg-muted-foreground rounded-full" style={{ animationDelay: "0.4s" }} />
                </span>
              ) : "")}
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 border-t border-white/10 bg-[#18181b]">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={blocked}
            placeholder={blocked ? "Receptionist unavailable" : "Type your message…"}
            className="flex-1 min-w-0 bg-[#0f0f11] border border-white/10 rounded-sm px-4 py-2.5 text-base sm:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200 disabled:opacity-50"
          />
          <Button data-testid="chat-send-btn" onMouseDown={(e) => e.preventDefault()} onClick={send} disabled={streaming || blocked} className="rounded-sm bg-primary hover:bg-orange-600 text-white px-4 shrink-0">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
